import type { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import { DocumentationViewItem, DocumentationViewItemType } from 'store/documentation/model/types';
import { DirectoryModel } from 'shared/domain/directory/directoryModel';
import { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import { sortItems } from 'components/common/dataGrid/sorting';
import { DocumentState, GridColumnFieldDocumentation } from 'components/common/dataGrid/columnsFactory/types';
import type { DocumentationValueGetters } from 'components/common/dataGrid/columnsFactory/valueGetters';
import { mapToViewItem } from 'components/documentation/DocumentationDataGrid/model';
import { hasValidId } from 'shared/utils/id/entityIds';
import { isSuccessUpload } from 'shared/domain/documentation/documentationVersionHelpers';
import { FiltersSliceState, ViewRowsSource } from './types';
import { toJSDate } from 'shared/utils/date';
import { normalizeForSearch } from 'shared/utils/search';

/**
 * Value getters that need no React context (no intl/users). Used for sorting
 * until the grid provides the real getters (`setSortValueGetters`).
 */
export const MINIMAL_VALUE_GETTERS: DocumentationValueGetters = {
  modifiedAt: (_field: string, row: DocumentationModel) => toJSDate(row.modifiedAt),
  createdAt: (_field: string, row: DocumentationModel) => toJSDate(row.createdAt),
  createdBy: (_field: string, row: DocumentationModel) => {
    const createdBy = row.createdBy;
    return createdBy?._id ? [{ _id: createdBy._id, label: createdBy._id }] : [];
  },
  modifiedBy: (_field: string, row: DocumentationModel) => {
    const modifiedBy = row.modifiedBy;
    return modifiedBy?._id ? [{ _id: modifiedBy._id, label: modifiedBy._id }] : [];
  },
  sites: (_field: string, row: DocumentationModel) => row.sites || [],
  levels: (_field: string, row: DocumentationModel) => row.levels || [],
  state: (_field: string, row: DocumentationModel) => row.deleted ? DocumentState.archived : DocumentState.published,
};

export const FILTER_OPTIONS = { fixedFilterField: GridColumnFieldDocumentation.state };

export const DEFAULT_FILTER_MODEL: GridFilterModel = {
  items: [],
  logicOperator: 'and' as GridFilterModel['logicOperator'],
};

export const DEFAULT_SORT_MODEL: GridSortModel = [
  { field: GridColumnFieldDocumentation.name, sort: 'asc' },
];

export const INITIAL_FILTERS_STATE: FiltersSliceState = {
  documentState: DocumentState.published,
  filterModel: DEFAULT_FILTER_MODEL,
  sortModel: DEFAULT_SORT_MODEL,
  searchProjectId: '',
  searchPhrase: '',
  filteredRows: [],
  baseRows: [],
  searchedDirectories: [],
  searchedDocumentations: [],
  isLoadingSearch: false,
  isFiltering: false,
};

/**
 * Normalized search query used for matching. Empty when the phrase has no
 * searchable characters (e.g. only spaces) - such a phrase is not a search.
 */
export function getDocumentationSearchQuery(phrase: string | undefined): string {
  return normalizeForSearch(phrase ?? '').trim();
}

/**
 * Whether the grid shows search results instead of the current directory.
 * While searching, `filteredRows` holds search results and directory fetches
 * must not overwrite them.
 */
export function isSearchActive(state: Pick<FiltersSliceState, 'searchPhrase'>): boolean {
  return getDocumentationSearchQuery(state.searchPhrase).length > 0;
}

/**
 * Unsorted grid rows from raw directories and documentations: drops items that
 * cannot be rendered and documents outside the selected state.
 */
export function buildViewRows({
  directories,
  documentations,
  documentState,
}: ViewRowsSource): DocumentationViewItem[] {
  const renderableDocumentations = filterRenderableDocumentations(documentations);
  return createViewItems(
    filterRenderableDirectories(directories),
    filterDocumentationsByState(renderableDocumentations, documentState)
  );
}

export function filterRenderableDocumentations(
  docs: DocumentationModel[]
): DocumentationModel[] {
  return docs.filter((doc) => hasValidId(doc) && isSuccessUpload(doc));
}

export function filterRenderableDirectories(
  dirs: DirectoryModel[]
): DirectoryModel[] {
  return dirs.filter(hasValidId);
}

export function filterDocumentationsByState(
  docs: DocumentationModel[],
  state: DocumentState
): DocumentationModel[] {
  if (state === DocumentState.archived) {
    return docs.filter((doc) => Boolean(doc.deleted));
  }

  return docs.filter((doc) => !doc.deleted);
}

export function createViewItems(
  dirs: DirectoryModel[],
  docs: DocumentationModel[]
): DocumentationViewItem[] {
  return [
    ...mapToViewItem(dirs, DocumentationViewItemType.DIRECTORY),
    ...mapToViewItem(docs, DocumentationViewItemType.FILE),
  ];
}

/**
 * Sort model actually applied: the grid's choice, or name ascending when there
 * is none - so every code path shows rows in the same order.
 */
export function resolveSortModel(sortModel: GridSortModel | undefined): GridSortModel {
  return sortModel?.length && sortModel[0]?.sort ? sortModel : DEFAULT_SORT_MODEL;
}

export function applySorting(
  items: DocumentationViewItem[],
  sortModel: GridSortModel,
  valueGetters: DocumentationValueGetters
): DocumentationViewItem[] {
  return sortItems(items, resolveSortModel(sortModel), valueGetters);
}

/**
 * Applies a row transformation to both the unsorted rows and the visible
 * (sorted) rows, so a later local re-sort keeps the change.
 */
export function mapVisibleRows(
  state: Pick<FiltersSliceState, 'baseRows' | 'filteredRows'>,
  transform: (rows: DocumentationViewItem[]) => DocumentationViewItem[]
): Pick<FiltersSliceState, 'baseRows' | 'filteredRows'> {
  return {
    baseRows: transform(state.baseRows),
    filteredRows: transform(state.filteredRows),
  };
}
